import { useState } from 'react';
import { Users } from 'lucide-react';
import type { LinkClickersFilter } from '../../lib/emailEngagement';
import LinkClickersModal from './LinkClickersModal';

interface LinkClickRow {
  url: string;
  uniqueClickers: number;
  totalClicks: number;
}

/** Display label: drop query string; full URL stays in href/title. */
function formatLinkDisplayUrl(url: string): string {
  if (url.startsWith('(')) return url;
  const queryIndex = url.indexOf('?');
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

interface ClicksByLinkTableProps {
  linkClicks: LinkClickRow[];
  filter: LinkClickersFilter;
  /** When false (rollup), individual clickers are unavailable. */
  canViewClickers?: boolean;
  timeZone?: string;
  /** Max rows to show before truncating. */
  limit?: number;
  title?: string;
  cellPaddingClass?: string;
  compact?: boolean;
}

export default function ClicksByLinkTable({
  linkClicks,
  filter,
  canViewClickers = true,
  timeZone,
  limit = 10,
  title = 'Clicks by link',
  cellPaddingClass = 'px-4 sm:px-5',
  compact = false,
}: ClicksByLinkTableProps) {
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  if (linkClicks.length === 0) return null;

  return (
    <>
      <div className={compact ? '' : 'border-t border-gray-200 dark:border-gray-700 overflow-hidden'}>
        <div
          className={`${cellPaddingClass} py-2 bg-gray-50 dark:bg-gray-900/40 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400`}
        >
          {title}
        </div>
        <table className={`w-full table-fixed ${compact ? 'text-xs' : 'text-sm'}`}>
          <thead className="text-xs text-gray-500 dark:text-gray-400">
            <tr>
              <th className={`text-left ${cellPaddingClass} py-1.5 font-medium`}>URL</th>
              <th className={`text-right ${cellPaddingClass} py-1.5 font-medium w-24`}>
                Clickers
              </th>
              <th className={`text-right ${cellPaddingClass} py-1.5 font-medium w-20`}>Clicks</th>
              <th className={`text-right ${cellPaddingClass} py-1.5 font-medium w-16`} aria-label="View clickers" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {linkClicks.slice(0, limit).map((row) => (
              <tr key={row.url} className="text-gray-800 dark:text-gray-200">
                <td className={`${cellPaddingClass} py-2 min-w-0`}>
                  <a
                    href={row.url.startsWith('(') ? undefined : row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 dark:text-emerald-400 hover:underline truncate block"
                    title={row.url}
                  >
                    {formatLinkDisplayUrl(row.url)}
                  </a>
                </td>
                <td className={`${cellPaddingClass} py-2 text-right tabular-nums`}>
                  {row.uniqueClickers}
                </td>
                <td className={`${cellPaddingClass} py-2 text-right tabular-nums`}>
                  {row.totalClicks}
                </td>
                <td className={`${cellPaddingClass} py-2 text-right`}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUrl(row.url);
                    }}
                    disabled={!canViewClickers}
                    title={
                      canViewClickers
                        ? 'View people who clicked this link'
                        : 'Individual clickers unavailable after rollup'
                    }
                    aria-label={
                      canViewClickers
                        ? `View people who clicked ${row.url}`
                        : 'Individual clickers unavailable after rollup'
                    }
                    className="inline-flex items-center justify-center p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Users className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedUrl ? (
        <LinkClickersModal
          isOpen
          onClose={() => setSelectedUrl(null)}
          destinationUrl={selectedUrl}
          filter={filter}
          timeZone={timeZone}
        />
      ) : null}
    </>
  );
}
